import * as echarts from 'echarts';
import { getCountryName } from './worldMapGeoJson';

/**
 * Regional (sub-national) maps rendered by the map widget when
 * `mapConfig.mapType` is set to a country key instead of 'world'.
 *
 * Each file in public/maps/{key}.json is a FeatureCollection with two layers
 * in draw order: neighbor-country context features (marked `ctx: 1`, silent
 * gray backdrop) and admin-1 region features named by ISO 3166-2 code.
 *
 * Sources: geoBoundaries UKR ADM1 / RUS ADM1 (gbOpen), deutschlandGeoJSON
 * (GeoBasis-DE, dl-de/by-2-0), world-atlas 50m for context countries.
 *
 * The Russia map ships as two files (russia_subjects.json / russia_districts.json)
 * selected via RegionalMapOptions.federalLevel. It contains only the 83
 * internationally recognized federal subjects — Crimea, Sevastopol and the
 * occupied Ukrainian oblasts are part of the Ukraine context feature. Features
 * carrying `kgd: 1` hold the Kaliningrad exclave: dropped when
 * excludeKaliningrad is set, and at district level merged into the RU-SZFO
 * mainland feature otherwise. Longitudes east of the antimeridian are shifted
 * +360 (Chukotka spans up to ~191°) so the country renders contiguously.
 */

export type RegionalMapKey = 'ukraine' | 'germany' | 'russia';

export const REGIONAL_MAP_KEYS: RegionalMapKey[] = ['ukraine', 'germany', 'russia'];

export interface RegionalMapOptions {
  /** Russia only: administrative level to load. Default 'subjects'. */
  federalLevel?: 'subjects' | 'districts';
  /** Russia only: drop the Kaliningrad exclave. Default false. */
  excludeKaliningrad?: boolean;
}

/** ISO 3166-2 code → English display name for every mapped region. */
const REGION_NAMES: Record<string, string> = {
  'UA-05': 'Vinnytsia Oblast',
  'UA-07': 'Volyn Oblast',
  'UA-09': 'Luhansk Oblast',
  'UA-12': 'Dnipropetrovsk Oblast',
  'UA-14': 'Donetsk Oblast',
  'UA-18': 'Zhytomyr Oblast',
  'UA-21': 'Zakarpattia Oblast',
  'UA-23': 'Zaporizhzhia Oblast',
  'UA-26': 'Ivano-Frankivsk Oblast',
  'UA-30': 'Kyiv',
  'UA-32': 'Kyiv Oblast',
  'UA-35': 'Kirovohrad Oblast',
  'UA-40': 'Sevastopol',
  'UA-43': 'Autonomous Republic of Crimea',
  'UA-46': 'Lviv Oblast',
  'UA-48': 'Mykolaiv Oblast',
  'UA-51': 'Odesa Oblast',
  'UA-53': 'Poltava Oblast',
  'UA-56': 'Rivne Oblast',
  'UA-59': 'Sumy Oblast',
  'UA-61': 'Ternopil Oblast',
  'UA-63': 'Kharkiv Oblast',
  'UA-65': 'Kherson Oblast',
  'UA-68': 'Khmelnytskyi Oblast',
  'UA-71': 'Cherkasy Oblast',
  'UA-74': 'Chernihiv Oblast',
  'UA-77': 'Chernivtsi Oblast',
  'DE-BW': 'Baden-Württemberg',
  'DE-BY': 'Bavaria',
  'DE-BE': 'Berlin',
  'DE-BB': 'Brandenburg',
  'DE-HB': 'Bremen',
  'DE-HH': 'Hamburg',
  'DE-HE': 'Hesse',
  'DE-MV': 'Mecklenburg-Vorpommern',
  'DE-NI': 'Lower Saxony',
  'DE-NW': 'North Rhine-Westphalia',
  'DE-RP': 'Rhineland-Palatinate',
  'DE-SL': 'Saarland',
  'DE-SN': 'Saxony',
  'DE-ST': 'Saxony-Anhalt',
  'DE-SH': 'Schleswig-Holstein',
  'DE-TH': 'Thuringia',
  'RU-AD': 'Adygea',
  'RU-AL': 'Altai Republic',
  'RU-ALT': 'Altai Krai',
  'RU-AMU': 'Amur Oblast',
  'RU-ARK': 'Arkhangelsk Oblast',
  'RU-AST': 'Astrakhan Oblast',
  'RU-BA': 'Bashkortostan',
  'RU-BEL': 'Belgorod Oblast',
  'RU-BRY': 'Bryansk Oblast',
  'RU-BU': 'Buryatia',
  'RU-CE': 'Chechnya',
  'RU-CHE': 'Chelyabinsk Oblast',
  'RU-CHU': 'Chukotka Autonomous Okrug',
  'RU-CU': 'Chuvashia',
  'RU-DA': 'Dagestan',
  'RU-IN': 'Ingushetia',
  'RU-IRK': 'Irkutsk Oblast',
  'RU-IVA': 'Ivanovo Oblast',
  'RU-KAM': 'Kamchatka Krai',
  'RU-KB': 'Kabardino-Balkaria',
  'RU-KC': 'Karachay-Cherkessia',
  'RU-KDA': 'Krasnodar Krai',
  'RU-KEM': 'Kemerovo Oblast',
  'RU-KGD': 'Kaliningrad Oblast',
  'RU-KGN': 'Kurgan Oblast',
  'RU-KHA': 'Khabarovsk Krai',
  'RU-KHM': 'Khanty-Mansi Autonomous Okrug',
  'RU-KIR': 'Kirov Oblast',
  'RU-KK': 'Khakassia',
  'RU-KL': 'Kalmykia',
  'RU-KLU': 'Kaluga Oblast',
  'RU-KO': 'Komi Republic',
  'RU-KOS': 'Kostroma Oblast',
  'RU-KR': 'Republic of Karelia',
  'RU-KRS': 'Kursk Oblast',
  'RU-KYA': 'Krasnoyarsk Krai',
  'RU-LEN': 'Leningrad Oblast',
  'RU-LIP': 'Lipetsk Oblast',
  'RU-MAG': 'Magadan Oblast',
  'RU-ME': 'Mari El',
  'RU-MO': 'Republic of Mordovia',
  'RU-MOS': 'Moscow Oblast',
  'RU-MOW': 'Moscow',
  'RU-MUR': 'Murmansk Oblast',
  'RU-NEN': 'Nenets Autonomous Okrug',
  'RU-NGR': 'Novgorod Oblast',
  'RU-NIZ': 'Nizhny Novgorod Oblast',
  'RU-NVS': 'Novosibirsk Oblast',
  'RU-OMS': 'Omsk Oblast',
  'RU-ORE': 'Orenburg Oblast',
  'RU-ORL': 'Oryol Oblast',
  'RU-PER': 'Perm Krai',
  'RU-PNZ': 'Penza Oblast',
  'RU-PRI': 'Primorsky Krai',
  'RU-PSK': 'Pskov Oblast',
  'RU-ROS': 'Rostov Oblast',
  'RU-RYA': 'Ryazan Oblast',
  'RU-SA': 'Sakha Republic (Yakutia)',
  'RU-SAK': 'Sakhalin Oblast',
  'RU-SAM': 'Samara Oblast',
  'RU-SAR': 'Saratov Oblast',
  'RU-SE': 'North Ossetia–Alania',
  'RU-SMO': 'Smolensk Oblast',
  'RU-SPE': 'Saint Petersburg',
  'RU-STA': 'Stavropol Krai',
  'RU-SVE': 'Sverdlovsk Oblast',
  'RU-TA': 'Tatarstan',
  'RU-TAM': 'Tambov Oblast',
  'RU-TOM': 'Tomsk Oblast',
  'RU-TUL': 'Tula Oblast',
  'RU-TVE': 'Tver Oblast',
  'RU-TY': 'Tuva',
  'RU-TYU': 'Tyumen Oblast',
  'RU-UD': 'Udmurtia',
  'RU-ULY': 'Ulyanovsk Oblast',
  'RU-VGG': 'Volgograd Oblast',
  'RU-VLA': 'Vladimir Oblast',
  'RU-VLG': 'Vologda Oblast',
  'RU-VOR': 'Voronezh Oblast',
  'RU-YAN': 'Yamalo-Nenets Autonomous Okrug',
  'RU-YAR': 'Yaroslavl Oblast',
  'RU-YEV': 'Jewish Autonomous Oblast',
  'RU-ZAB': 'Zabaykalsky Krai',
  'RU-CFO': 'Central Federal District',
  'RU-SZFO': 'Northwestern Federal District',
  'RU-YUFO': 'Southern Federal District',
  'RU-SKFO': 'North Caucasian Federal District',
  'RU-PFO': 'Volga Federal District',
  'RU-UFO': 'Ural Federal District',
  'RU-SFO': 'Siberian Federal District',
  'RU-DFO': 'Far Eastern Federal District',
};

/**
 * Display name for a map polygon: ISO 3166-2 region or a context country's
 * alpha-2 code. Falls back to the code itself.
 */
export function getRegionName(code: string): string {
  return REGION_NAMES[code] ?? getCountryName(code);
}

export interface CityMarker {
  name: string;
  /** [longitude, latitude] */
  coord: [number, number];
}

/** Curated city markers per regional map (region capitals + major cities). */
const CITIES: Record<RegionalMapKey, CityMarker[]> = {
  ukraine: [
    { name: 'Kyiv', coord: [30.523, 50.45] },
    { name: 'Kharkiv', coord: [36.23, 49.994] },
    { name: 'Odesa', coord: [30.723, 46.483] },
    { name: 'Dnipro', coord: [35.046, 48.465] },
    { name: 'Donetsk', coord: [37.803, 48.016] },
    { name: 'Zaporizhzhia', coord: [35.14, 47.839] },
    { name: 'Lviv', coord: [24.03, 49.84] },
    { name: 'Mykolaiv', coord: [31.995, 46.975] },
    { name: 'Luhansk', coord: [39.308, 48.574] },
    { name: 'Vinnytsia', coord: [28.468, 49.233] },
    { name: 'Simferopol', coord: [34.111, 44.952] },
    { name: 'Sevastopol', coord: [33.525, 44.617] },
    { name: 'Kherson', coord: [32.618, 46.635] },
    { name: 'Poltava', coord: [34.551, 49.588] },
    { name: 'Chernihiv', coord: [31.289, 51.498] },
    { name: 'Cherkasy', coord: [32.06, 49.444] },
    { name: 'Zhytomyr', coord: [28.659, 50.255] },
    { name: 'Sumy', coord: [34.798, 50.908] },
    { name: 'Rivne', coord: [26.252, 50.62] },
    { name: 'Ivano-Frankivsk', coord: [24.711, 48.923] },
    { name: 'Ternopil', coord: [25.595, 49.554] },
    { name: 'Lutsk', coord: [25.342, 50.747] },
    { name: 'Uzhhorod', coord: [22.288, 48.621] },
    { name: 'Khmelnytskyi', coord: [26.987, 49.423] },
    { name: 'Chernivtsi', coord: [25.94, 48.292] },
    { name: 'Kropyvnytskyi', coord: [32.26, 48.508] },
  ],
  germany: [
    { name: 'Berlin', coord: [13.405, 52.52] },
    { name: 'Hamburg', coord: [9.994, 53.551] },
    { name: 'Munich', coord: [11.582, 48.135] },
    { name: 'Cologne', coord: [6.96, 50.938] },
    { name: 'Frankfurt', coord: [8.682, 50.111] },
    { name: 'Stuttgart', coord: [9.183, 48.776] },
    { name: 'Düsseldorf', coord: [6.774, 51.228] },
    { name: 'Leipzig', coord: [12.373, 51.34] },
    { name: 'Dresden', coord: [13.737, 51.05] },
    { name: 'Hanover', coord: [9.732, 52.376] },
    { name: 'Nuremberg', coord: [11.077, 49.452] },
    { name: 'Bremen', coord: [8.802, 53.079] },
    { name: 'Wiesbaden', coord: [8.24, 50.078] },
    { name: 'Kiel', coord: [10.123, 54.323] },
    { name: 'Magdeburg', coord: [11.628, 52.121] },
    { name: 'Erfurt', coord: [11.03, 50.979] },
    { name: 'Mainz', coord: [8.247, 49.993] },
    { name: 'Saarbrücken', coord: [6.997, 49.24] },
    { name: 'Schwerin', coord: [11.415, 53.636] },
    { name: 'Potsdam', coord: [13.065, 52.391] },
  ],
  russia: [
    { name: 'Moscow', coord: [37.618, 55.752] },
    { name: 'Saint Petersburg', coord: [30.316, 59.939] },
    { name: 'Kaliningrad', coord: [20.51, 54.71] },
    { name: 'Murmansk', coord: [33.08, 68.97] },
    { name: 'Arkhangelsk', coord: [40.54, 64.54] },
    { name: 'Voronezh', coord: [39.2, 51.66] },
    { name: 'Nizhny Novgorod', coord: [44.0, 56.33] },
    { name: 'Kazan', coord: [49.11, 55.79] },
    { name: 'Samara', coord: [50.15, 53.2] },
    { name: 'Saratov', coord: [46.03, 51.53] },
    { name: 'Volgograd', coord: [44.51, 48.71] },
    { name: 'Rostov-on-Don', coord: [39.71, 47.24] },
    { name: 'Krasnodar', coord: [38.98, 45.04] },
    { name: 'Ufa', coord: [55.96, 54.74] },
    { name: 'Perm', coord: [56.25, 58.01] },
    { name: 'Yekaterinburg', coord: [60.6, 56.84] },
    { name: 'Chelyabinsk', coord: [61.4, 55.16] },
    { name: 'Tyumen', coord: [65.53, 57.15] },
    { name: 'Omsk', coord: [73.37, 54.99] },
    { name: 'Novosibirsk', coord: [82.92, 55.03] },
    { name: 'Krasnoyarsk', coord: [92.87, 56.01] },
    { name: 'Irkutsk', coord: [104.28, 52.29] },
    { name: 'Yakutsk', coord: [129.73, 62.03] },
    { name: 'Khabarovsk', coord: [135.07, 48.48] },
    { name: 'Vladivostok', coord: [131.89, 43.12] },
    { name: 'Magadan', coord: [150.8, 59.56] },
    { name: 'Petropavlovsk-Kamchatsky', coord: [158.65, 53.02] },
    { name: 'Anadyr', coord: [177.51, 64.73] },
  ],
};

export function getRegionalCities(key: RegionalMapKey): CityMarker[] {
  return CITIES[key];
}

/**
 * Tight country bounds (+ small margin) per map as [[west, north], [east, south]].
 * ChartMap expands one dimension to the widget's aspect ratio so the map fills
 * the whole widget, with context countries bleeding to the edges.
 */
export const REGIONAL_CORE_BOUNDS: Record<
  RegionalMapKey,
  [[number, number], [number, number]]
> = {
  ukraine: [
    [21.9, 52.6],
    [40.4, 44.0],
  ],
  germany: [
    [5.6, 55.3],
    [15.3, 47.0],
  ],
  russia: [
    [19.3, 82.1],
    [191.4, 41.0],
  ],
};

/** Russia bounds without the Kaliningrad exclave: west edge at the Pskov border. */
const RUSSIA_CORE_BOUNDS_NO_KGD: [[number, number], [number, number]] = [
  [26.8, 82.1],
  [191.4, 41.0],
];

/** Core bounds for a regional map, accounting for variant options. */
export function getRegionalCoreBounds(
  key: RegionalMapKey,
  options?: RegionalMapOptions
): [[number, number], [number, number]] {
  if (key === 'russia' && options?.excludeKaliningrad) return RUSSIA_CORE_BOUNDS_NO_KGD;
  return REGIONAL_CORE_BOUNDS[key];
}

/** Must match the geo component's aspectScale (ECharts default 0.75). */
export const GEO_ASPECT_SCALE = 0.75;

/**
 * Expands core bounds symmetrically in one dimension until the box's screen
 * aspect ratio ((Δlng × aspectScale) / Δlat) equals the container's, so
 * ECharts' fit-to-viewport fills the widget exactly instead of letterboxing.
 */
export function expandBoundsToAspect(
  core: [[number, number], [number, number]],
  width: number,
  height: number
): [[number, number], [number, number]] {
  const [[west, north], [east, south]] = core;
  const dLng = east - west;
  const dLat = north - south;
  const target = width / height;
  const current = (dLng * GEO_ASPECT_SCALE) / dLat;
  if (target > current) {
    const pad = ((target * dLat) / GEO_ASPECT_SCALE - dLng) / 2;
    return [
      [west - pad, north],
      [east + pad, south],
    ];
  }
  const pad = ((dLng * GEO_ASPECT_SCALE) / target - dLat) / 2;
  // Latitudes past the poles break ECharts' geo layout (the map gets cropped
  // instead of fitted) — clamp at ±90 and push the excess to the other side.
  // Reachable on very wide maps (Russia spans ~172° of longitude).
  let expandedNorth = north + pad;
  let expandedSouth = south - pad;
  if (expandedNorth > 90) {
    expandedSouth -= expandedNorth - 90;
    expandedNorth = 90;
  }
  if (expandedSouth < -90) {
    expandedNorth = Math.min(90, expandedNorth + (-90 - expandedSouth));
    expandedSouth = -90;
  }
  return [
    [west, expandedNorth],
    [east, expandedSouth],
  ];
}

export interface LoadedRegionalMap {
  /** Name the GeoJSON is registered under in ECharts. */
  mapName: string;
  /** Data-joinable ISO 3166-2 region codes, in feature draw order. */
  regionCodes: string[];
  /** Alpha-2 codes of the silent neighbor-country backdrop features. */
  contextCodes: string[];
}

interface MapFeature {
  type: 'Feature';
  properties: { name: string; ctx?: number; kgd?: number };
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: any[] };
}

interface MapFeatureCollection {
  type: 'FeatureCollection';
  features: MapFeature[];
}

/** Map file basename in public/maps/ for a key + options combination. */
function resolveFileKey(key: RegionalMapKey, options?: RegionalMapOptions): string {
  if (key !== 'russia') return key;
  return options?.federalLevel === 'districts' ? 'russia_districts' : 'russia_subjects';
}

function asMultiPolygonCoords(geometry: MapFeature['geometry']): any[] {
  return geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
}

/**
 * Resolves `kgd: 1` (Kaliningrad exclave) features for a variant: dropped when
 * excluded; otherwise merged into a same-named mainland feature when one exists
 * (district level), or kept as a standalone region (subject level).
 */
function buildVariant(raw: MapFeatureCollection, excludeKaliningrad: boolean): MapFeatureCollection {
  const kgdFeatures = raw.features.filter((f) => f.properties.kgd);
  if (!kgdFeatures.length) return raw;
  if (excludeKaliningrad) {
    return { ...raw, features: raw.features.filter((f) => !f.properties.kgd) };
  }
  const features = raw.features
    .filter(
      (f) =>
        !f.properties.kgd ||
        !raw.features.some((o) => o !== f && !o.properties.kgd && o.properties.name === f.properties.name)
    )
    .map((f) => {
      if (f.properties.kgd) return f;
      const exclave = kgdFeatures.find((e) => e !== f && e.properties.name === f.properties.name);
      if (!exclave) return f;
      return {
        ...f,
        geometry: {
          type: 'MultiPolygon' as const,
          coordinates: [...asMultiPolygonCoords(f.geometry), ...asMultiPolygonCoords(exclave.geometry)],
        },
      };
    });
  return { ...raw, features };
}

const fetchedFiles: Record<string, Promise<MapFeatureCollection>> = {};
const loadedMaps: Record<string, Promise<LoadedRegionalMap>> = {};

function fetchMapFile(fileKey: string): Promise<MapFeatureCollection> {
  const existing = fetchedFiles[fileKey];
  if (existing) return existing;
  const promise = fetch(`${process.env.PUBLIC_URL || ''}/maps/${fileKey}.json`).then((res) => {
    if (!res.ok) throw new Error(`Failed to load map "${fileKey}": HTTP ${res.status}`);
    return res.json();
  });
  // Allow retry after a failed fetch instead of caching the rejection
  promise.catch(() => {
    if (fetchedFiles[fileKey] === promise) delete fetchedFiles[fileKey];
  });
  fetchedFiles[fileKey] = promise;
  return promise;
}

/**
 * Fetches a regional map file, registers the requested variant with ECharts
 * and returns its feature inventory. The fetch runs once per file and the
 * registration once per variant; concurrent and subsequent callers share the
 * same promise.
 */
export function loadRegionalMap(
  key: RegionalMapKey,
  options?: RegionalMapOptions
): Promise<LoadedRegionalMap> {
  const fileKey = resolveFileKey(key, options);
  const excludeKaliningrad = key === 'russia' && Boolean(options?.excludeKaliningrad);
  const mapName = `region_${fileKey}${excludeKaliningrad ? '_nokgd' : ''}`;

  const existing = loadedMaps[mapName];
  if (existing) return existing;

  const promise = fetchMapFile(fileKey).then((raw) => {
    const geoJson = buildVariant(raw, excludeKaliningrad);
    echarts.registerMap(mapName, geoJson as any);
    const features = geoJson.features;
    return {
      mapName,
      regionCodes: Array.from(
        new Set(features.filter((f) => !f.properties.ctx).map((f) => f.properties.name))
      ),
      contextCodes: features.filter((f) => f.properties.ctx).map((f) => f.properties.name),
    };
  });

  promise.catch(() => {
    if (loadedMaps[mapName] === promise) delete loadedMaps[mapName];
  });

  loadedMaps[mapName] = promise;
  return promise;
}
